"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { Download, Share, X } from "lucide-react";

import { useI18n } from "@/lib/i18n";
import {
  hasInstallPromptDecision,
  installMode,
  rememberInstallPromptDecision,
  shouldReloadForControllerChange,
  type InstallMode
} from "@/lib/pwa";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

function isStandalone(): boolean {
  const navigatorWithStandalone = navigator as Navigator & { standalone?: boolean };
  return Boolean(navigatorWithStandalone.standalone) || window.matchMedia("(display-mode: standalone)").matches;
}

export function PwaManager() {
  const { t } = useI18n();
  const [installEvent, setInstallEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [mode, setMode] = useState<InstallMode>("none");
  const automaticUpdatePending = useRef(false);

  useEffect(() => {
    const refreshInstallMode = (promptAvailable: boolean) => {
      const installPromptDecided = hasInstallPromptDecision(window.localStorage);
      const next = installMode({
        userAgent: navigator.userAgent,
        maxTouchPoints: navigator.maxTouchPoints,
        standalone: isStandalone(),
        promptAvailable
      });
      setMode(installPromptDecided ? "none" : next);
    };
    refreshInstallMode(false);

    const onInstallPrompt = (event: Event) => {
      const promptEvent = event as BeforeInstallPromptEvent;
      promptEvent.preventDefault();
      if (hasInstallPromptDecision(window.localStorage)) return;
      setInstallEvent(promptEvent);
      refreshInstallMode(true);
    };
    const onInstalled = () => {
      rememberInstallPromptDecision(window.localStorage);
      setInstallEvent(null);
      setMode("none");
    };
    window.addEventListener("beforeinstallprompt", onInstallPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onInstallPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;

    if (!import.meta.env.PROD) {
      void navigator.serviceWorker.getRegistrations().then((registrations) =>
        Promise.all(registrations.map((registration) => registration.unregister()))
      );
      return;
    }

    let registration: ServiceWorkerRegistration | undefined;
    let updateTimer: number | undefined;
    const activateWaitingWorker = () => {
      if (navigator.serviceWorker.controller && registration?.waiting) {
        automaticUpdatePending.current = true;
        registration.waiting.postMessage({ type: "SKIP_WAITING" });
      }
    };
    const register = async () => {
      registration = await navigator.serviceWorker.register("/sw.js", {
        scope: "/",
        updateViaCache: "none"
      });
      activateWaitingWorker();
      registration.addEventListener("updatefound", () => {
        registration?.installing?.addEventListener("statechange", activateWaitingWorker);
      });
      await registration.update();
      activateWaitingWorker();
      updateTimer = window.setInterval(() => {
        void registration?.update().catch(() => undefined);
      }, 5 * 60 * 1_000);
    };
    void register().catch(() => {
      // The web app remains fully usable when registration is unavailable.
    });

    const reloadAfterAutomaticActivation = () => {
      if (shouldReloadForControllerChange(automaticUpdatePending.current)) {
        automaticUpdatePending.current = false;
        window.location.reload();
      }
    };
    navigator.serviceWorker.addEventListener("controllerchange", reloadAfterAutomaticActivation);
    return () => {
      if (updateTimer !== undefined) window.clearInterval(updateTimer);
      navigator.serviceWorker.removeEventListener("controllerchange", reloadAfterAutomaticActivation);
    };
  }, []);

  const install = useCallback(async () => {
    if (!installEvent) return;
    rememberInstallPromptDecision(window.localStorage);
    setInstallEvent(null);
    setMode("none");
    await installEvent.prompt();
    await installEvent.userChoice;
  }, [installEvent]);

  const dismissInstall = () => {
    rememberInstallPromptDecision(window.localStorage);
    setInstallEvent(null);
    setMode("none");
  };

  return (
    <>
      {mode === "native-prompt" && installEvent ? (
        <section role="status" aria-label={t("pwa.install.aria")} aria-live="polite" className="fixed inset-x-4 bottom-[calc(5.5rem+env(safe-area-inset-bottom))] z-40 mx-auto max-w-md rounded-lg border border-[var(--hc-border-strong)] bg-[var(--hc-surface-2)] p-4 shadow-2xl lg:bottom-5">
          <PwaNotice icon={Download} title={t("pwa.install.title")} detail={t("pwa.install.detail")} onDismiss={dismissInstall} dismissLabel={t("pwa.install.dismiss")}>
            <button type="button" onClick={install} className="min-h-11 cursor-pointer rounded-md bg-[var(--hc-blue)] px-4 text-sm font-semibold text-[#080a0f] transition-opacity hover:opacity-90">
              {t("pwa.install.action")}
            </button>
          </PwaNotice>
        </section>
      ) : null}

      {mode === "ios-manual" ? (
        <section role="status" aria-label={t("pwa.install.aria")} aria-live="polite" className="fixed inset-x-4 bottom-[calc(5.5rem+env(safe-area-inset-bottom))] z-40 mx-auto max-w-md rounded-lg border border-[var(--hc-border-strong)] bg-[var(--hc-surface-2)] p-4 shadow-2xl lg:bottom-5">
          <PwaNotice icon={Share} title={t("pwa.ios.title")} detail={t("pwa.ios.detail")} onDismiss={dismissInstall} dismissLabel={t("pwa.install.dismiss")} />
        </section>
      ) : null}
    </>
  );
}

function PwaNotice({
  icon: Icon,
  title,
  detail,
  onDismiss,
  dismissLabel,
  children
}: {
  icon: typeof Download;
  title: string;
  detail: string;
  onDismiss: () => void;
  dismissLabel: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="mt-0.5 shrink-0 text-[var(--hc-blue)]" size={20} aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-[var(--hc-text)]">{title}</p>
        <p className="mt-1 text-sm leading-5 text-[var(--hc-muted)]">{detail}</p>
        {children ? <div className="mt-3 flex flex-wrap gap-2">{children}</div> : null}
      </div>
      <button type="button" aria-label={dismissLabel} onClick={onDismiss} className="grid size-11 shrink-0 cursor-pointer place-items-center rounded-md text-[var(--hc-muted)] transition-colors hover:bg-[var(--hc-surface-3)] hover:text-[var(--hc-text)]">
        <X size={18} aria-hidden="true" />
      </button>
    </div>
  );
}
